// Copyright 2025 Sorama B.V.

import { autoinject, transient } from "aurelia-dependency-injection";
import { BindingEngine, computedFrom, Disposable } from "aurelia-framework";
import { ReportableMeasurement } from "datatypes/ReportableMeasurement";
import { SorXMeasurementTypeEnum } from "datatypes/SoramaWebApi";
import {
    GasType,
    GasTypesOptions,
    operatingConditions,
    SorXLDData,
    SorXMeasurement,
    SorXPDDData,
} from "datatypes/SorXMeasurement";
import { ObjectExtensions } from "extensions/ObjectExtensions";
import { IModal } from "libraries/WorkflowFramework/Interfaces/IModal";
import { IWorkflowModuleModalInstance } from "libraries/WorkflowFramework/Interfaces/IWorkflowModule";
import { ReportExService } from "services/ReportEx/ReportExService";
import { CompositeUnit } from "units/CompositeUnit";
import { Currency } from "units/Currency";
import { Mass, MassCost } from "units/Mass";
import { MassFlow } from "units/MassFlow";
import { ConversionContext, Unit, UnitValue } from "units/Unit";
import { UnitSystem } from "units/UnitSystem";
import { Volume, VolumeCost } from "units/Volume";
import { VolumeFlow } from "units/VolumeFlow";
import { MeasurementPropertyHelpers } from "./MeasurementPropertyHelpers";
import { MeasurementUnitHelpers } from "./MeasurementUnitHelpers";
import { UnitOptionsHelper } from "./UnitOptionsHelper";

/**
 * Allows for editing multiple underlying reports of the same type
 */
@transient()
@autoinject
export class EditMeasurementModal implements IModal<EditMeasurementModalState> {
    //#region Private Fields
    // Measurement is the current measurement being edited
    private measurement: ReportableMeasurement<SorXMeasurement> | null = null;
    // OriginalMeasurement is the measurement before any edits were made
    private originalMeasurement: ReportableMeasurement<SorXMeasurement> | null = null;

    private _selectedUnitSystem: UnitSystem;
    private _selectedCurrency: Unit;

    private gasTypeOptions: GasType[] = [...GasTypesOptions];

    protected unitSystemOptions = [UnitSystem.Metric, UnitSystem.Imperial];
    protected currencyOptions = Unit.getNamedList(Currency);
    protected operatingFrequencyOptions: string[] = operatingConditions;

    private modalInstance: IWorkflowModuleModalInstance<EditMeasurementModalState>;
    private disposables: Disposable[] = [];
    protected showSelectedMeasurements = false;
    private onMeasurementsChanged: (() => void) | null = null;
    private hasLeakRateConversionFeatureLicense = false;

    //#endregion

    //#region Properties
    private get selectedUnitSystem(): UnitSystem {
        return this.getSelectedUnitSystem(this.measurement);
    }

    private set selectedUnitSystem(value: UnitSystem) {
        if (this.selectedUnitSystem.name === value.name) return; // Avoid unnecessary updates
        this._selectedUnitSystem = value;
        (this.measurement.data as SorXLDData).tagLeakUnitSystem = value;
        MeasurementUnitHelpers.applyUnitSystemDefaults(this.measurement.data, this.selectedUnitSystem);
        // Make sure all currency units are still the selected currency (defaults might contain different currencies)
        MeasurementUnitHelpers.applyCurrency(this.measurement.data, this.selectedCurrency);
    }

    private get selectedCurrency(): Unit {
        return this.getSelectedCurrency(this.measurement);
    }

    private set selectedCurrency(value: Unit) {
        if (this.selectedCurrency.unit === value.unit) return; // Avoid unnecessary updates
        this._selectedCurrency = value;
        this.selectedUnitSystem.currency = this._selectedCurrency;
        MeasurementUnitHelpers.applyCurrency(this.measurement.data, this.selectedCurrency);
    }

    private get selectedGasType(): GasType {
        return (this.measurement.data as SorXLDData).tagLeakGasType;
    }

    private set selectedGasType(value: GasType) {
        (this.measurement.data as SorXLDData).tagLeakGasType = value;
    }

    @computedFrom("reportService.selectedReportIds", "measurement")
    private get numberOfSelectedMeasurements(): number {
        return this.reportService.selectedReports.length;
    }

    @computedFrom("reportService.selectedReportIds")
    private get selectedMeasurements(): ReportableMeasurement<SorXMeasurement>[] {
        this.setMeasurement();
        return this.reportService.selectedReports.map((r) => {
            const reportCopy = ObjectExtensions.copy(r);
            MeasurementUnitHelpers.rehydrateUnitValues(reportCopy.data as Record<string, unknown>);
            return reportCopy;
        });
    }

    //#endregion

    //#region Helper Methods and Property Accessors
    private hasMixedValues(path: string): boolean {
        return MeasurementPropertyHelpers.hasMixedValues(this.measurement, this.reportService.selectedReports, path);
    }

    // Simplified property accessors
    private get leakLinePressure() {
        if (this.hasMixedValues("tagLeakLinePressure.value")) return null;
        return (this.measurement.data as SorXLDData).tagLeakLinePressure?.value;
    }
    private set leakLinePressure(v: number) {
        (this.measurement.data as SorXLDData).tagLeakLinePressure.value = v;
    }

    private get leakGasCost() {
        if (this.hasMixedValues("tagLeakGasCost.value")) return null;
        return (this.measurement.data as SorXLDData).tagLeakGasCost?.value;
    }
    private set leakGasCost(v: number) {
        (this.measurement.data as SorXLDData).tagLeakGasCost.value = v;
    }

    private get leakElectricityCost() {
        if (this.hasMixedValues("tagLeakElectricityCost.value")) return null;
        return (this.measurement.data as SorXLDData).tagLeakElectricityCost?.value;
    }
    private set leakElectricityCost(v: number) {
        (this.measurement.data as SorXLDData).tagLeakElectricityCost.value = v;
    }

    private get leakPowerRatio() {
        if (this.hasMixedValues("tagLeakPowerRatio.value")) return null;
        return (this.measurement.data as SorXLDData).tagLeakPowerRatio?.value;
    }
    private set leakPowerRatio(v: number) {
        (this.measurement.data as SorXLDData).tagLeakPowerRatio.value = v;
    }

    private get leakOperatingHours() {
        if (this.hasMixedValues("tagLeakOperatingHours")) return null;
        return (this.measurement.data as SorXLDData).tagLeakOperatingHours;
    }
    private set leakOperatingHours(v: number) {
        (this.measurement.data as SorXLDData).tagLeakOperatingHours = v;
    }

    private get leakRateUnit(): Unit {
        return (this.measurement.data as SorXLDData).tagLeakRate?.unit;
    }
    private set leakRateUnit(newUnit: Unit) {
        const data = this.measurement.data as SorXLDData;
        const leakRate = data.tagLeakRate;
        if (!leakRate || leakRate.unit.unit === newUnit.unit) return;

        const wasMassFlow = leakRate.unit.unitType === MassFlow;
        const isNowMassFlow = newUnit.unitType === MassFlow;

        const gasType = data.tagLeakGasType;
        const convertedValue = leakRate.toUnit(newUnit, { gasType });
        data.tagLeakRate = new UnitValue(convertedValue, newUnit);

        // Auto-switch gas cost unit denomination to match leak rate type
        if (wasMassFlow !== isNowMassFlow) {
            const gasCostUnit = data.tagLeakGasCost?.unit as CompositeUnit;
            if (gasCostUnit) {
                const currency = gasCostUnit.numerator ?? this.selectedCurrency;
                if (isNowMassFlow) {
                    data.tagLeakGasCost = new UnitValue(
                        data.tagLeakGasCost.value,
                        new CompositeUnit(currency, Mass.gram, MassCost)
                    );
                } else {
                    data.tagLeakGasCost = new UnitValue(
                        data.tagLeakGasCost.value,
                        new CompositeUnit(currency, Volume.liters, VolumeCost)
                    );
                }
            }
        }
    }
    //#endregion

    //#region Constructor and Lifecycle

    constructor(
        private readonly reportService: ReportExService,
        private readonly bindingEngine: BindingEngine
    ) {}

    public activateModal(
        state: EditMeasurementModalState,
        modalInstance: IWorkflowModuleModalInstance<EditMeasurementModalState>
    ) {
        this.modalInstance = modalInstance;
        if (state && state.onMeasurementsChanged) {
            this.onMeasurementsChanged = state.onMeasurementsChanged;
            this.hasLeakRateConversionFeatureLicense = true;
        }
        this.setMeasurement();

        // Watch for changes in selectedReportIds to trigger measurement getter re-evaluation
        this.disposables.push(
            this.bindingEngine.collectionObserver(this.reportService.selectedReportIds).subscribe(() => {
                if (
                    this.reportService.hasNoMeasurementSelected ||
                    this.reportService.hasMixedMeasurementTypesSelected
                ) {
                    this.modalInstance.close();
                }
                this.setMeasurement();
            })
        );
    }

    protected toggleSelectedMeasurements(): void {
        this.showSelectedMeasurements = !this.showSelectedMeasurements;
    }

    private setMeasurement(): void {
        const measurement = this.reportService.getSingleOrDefault();

        // Edit on a copy of the measurement, edits get applied to actual measurements on save
        this.measurement = ObjectExtensions.copy(measurement);
        this.originalMeasurement = ObjectExtensions.copy(measurement);

        // structuredClone strips class prototypes, so UnitValue instances need to be restored
        MeasurementUnitHelpers.rehydrateUnitValues(this.measurement?.data as Record<string, unknown>);
        MeasurementUnitHelpers.rehydrateUnitValues(this.originalMeasurement?.data as Record<string, unknown>);

        // Ensure unit system and currency are properly initialized
        this._selectedUnitSystem = this.getSelectedUnitSystem(this.measurement);
        this._selectedCurrency = this.getSelectedCurrency(this.measurement);
    }

    public deactivate(): void {
        // Clean up all disposables when modal is closed
        this.disposables.forEach((disposable) => disposable.dispose());
        this.disposables = [];
    }

    //#endregion

    //#region Data Management
    protected saveChangesToReports(): void {
        if (!this.measurement) return;

        const type = this.measurement.data.type;

        const selectedReports = this.reportService.selectedReports;

        // If multiple reports are selected clone the information
        const sameTypeReports = selectedReports.filter((r) => r.data.type === type);

        for (let report of sameTypeReports) {
            // Rename if one measurement is selected and the name has changed
            // ID is updated since it might show in the report as well
            // Also reselect since ID changed
            if (
                this.numberOfSelectedMeasurements === 1 &&
                this.measurement.name != null &&
                report.name !== this.measurement.name
            ) {
                this.reportService.deselectReport(report.id);
                report.name = this.measurement.name;
                report.id = this.measurement.name;
                this.reportService.selectReport(report.id);
            }

            switch (type) {
                case SorXMeasurementTypeEnum.LeakDetection:
                    this.copyLeakDetectionData(report);
                    break;
                case SorXMeasurementTypeEnum.PartialDischarge:
                    this.copyPartialDischargeData(report);
                    break;
            }
            this.reportService.updateReport(report);
        }

        this.reportService.recalculateResults();

        // Trigger the callback to notify parent component of changes
        if (this.onMeasurementsChanged) {
            this.onMeasurementsChanged();
        }

        this.modalInstance.close();
    }
    //#endregion

    //#region Computed Properties
    @computedFrom("measurement")
    protected get pressureUnitOptions(): Unit[] {
        if (!this.measurement?.data) return [];
        const unitInMeasurement = (this.measurement.data as SorXLDData).tagLeakLinePressure?.unit;
        return UnitOptionsHelper.getPressureUnitOptions(unitInMeasurement);
    }

    protected get gasCostUnitOptions(): Unit[] {
        if (!this.measurement?.data) return [];

        const data = this.measurement.data as SorXLDData;
        const unitInMeasurement = data.tagLeakGasCost?.unit as CompositeUnit;
        const leakRateUnit = data.tagLeakRate?.unit;

        // Make the current unit have the selected currency as numerator
        // Use new UnitValue to bypass the .unit setter which would try to convert between unit families
        const updatedUnit = new CompositeUnit(
            this.selectedCurrency,
            unitInMeasurement.denominator,
            unitInMeasurement.unitType
        );
        data.tagLeakGasCost = new UnitValue(data.tagLeakGasCost.value, updatedUnit);

        const options = UnitOptionsHelper.getGasCostUnitOptions(
            unitInMeasurement,
            this.selectedCurrency,
            this._selectedUnitSystem,
            leakRateUnit
        );

        // If the current gas cost unit doesn't match the available options, switch to the first option
        const currentUnitStr = data.tagLeakGasCost.unit?.unit;
        if (options.length > 0 && !options.some((o) => o.unit === currentUnitStr)) {
            data.tagLeakGasCost = new UnitValue(data.tagLeakGasCost.value, options[0]);
        }

        return options;
    }

    @computedFrom("measurement", "_selectedUnitSystem", "_selectedCurrency")
    protected get electricityCostUnitOptions(): Unit[] {
        if (!this.measurement?.data) return [];

        const unitInMeasurement = (this.measurement.data as SorXLDData).tagLeakElectricityCost?.unit as CompositeUnit;

        // Make the current unit have the selected currency as numerator
        (this.measurement.data as SorXLDData).tagLeakElectricityCost.unit = new CompositeUnit(
            this.selectedCurrency,
            unitInMeasurement.denominator,
            unitInMeasurement.unitType
        );

        return UnitOptionsHelper.getElectricityCostUnitOptions(unitInMeasurement, this.selectedCurrency);
    }

    @computedFrom("measurement", "_selectedUnitSystem")
    protected get powerRatioUnitOptions(): Unit[] {
        if (!this.measurement?.data) return [];

        const unitInMeasurement = (this.measurement.data as SorXLDData).tagLeakPowerRatio?.unit;
        return UnitOptionsHelper.getPowerRatioUnitOptions(unitInMeasurement, this._selectedUnitSystem);
    }

    @computedFrom("measurement", "_selectedUnitSystem")
    protected get leakRateUnitOptions(): Unit[] {
        if (!this.measurement?.data) return [];
        const unit = (this.measurement.data as SorXLDData).tagLeakRate?.unit;
        if (!unit) return [];

        const allOptions = UnitOptionsHelper.getLeakRateUnitOptions(unit, this._selectedUnitSystem);
        return this.hasLeakRateConversionFeatureLicense
            ? allOptions
            : allOptions.filter((u) => u.unit === VolumeFlow.litersPerMinute.unit);
    }
    //#endregion

    //#region Data Copy Methods
    private copyLeakDetectionData(report: ReportableMeasurement<SorXMeasurement>): void {
        const source = this.measurement.data as SorXLDData;
        const target = report.data as SorXLDData;

        target.tagLeakUnitSystem = source.tagLeakUnitSystem;

        this.copyData(target, "tagLeakLinePressure");
        this.copyData(target, "tagLeakGasCost");
        this.copyData(target, "tagLeakElectricityCost");
        this.copyData(target, "tagLeakPowerRatio");

        target.tagLeakOperatingHours = source.tagLeakOperatingHours;
        target.tagLeakGasType = source.tagLeakGasType;

        // Special handling for leak rate conversion with gas type context
        const conversionContext: ConversionContext = { gasType: source.tagLeakGasType };
        target.tagLeakRate = new UnitValue(
            target.tagLeakRate.toUnit(source.tagLeakRate.unit, conversionContext),
            source.tagLeakRate.unit
        );
        target.tagLeakCost.unit = source.tagLeakCost.unit;
    }

    private copyData(target: SorXLDData, propertyName: string): void {
        const source = this.measurement.data as SorXLDData;
        const original = this.originalMeasurement?.data as SorXLDData;

        // if unit has changed
        if (MeasurementUnitHelpers.hasUnitChanged(source[propertyName], original[propertyName])) {
            // Special handling for leak rate conversion with gas type context
            if (propertyName === "tagLeakRate") {
                const conversionContext: ConversionContext = { gasType: source.tagLeakGasType };
                target[propertyName] = new UnitValue(
                    target[propertyName].toUnit(source[propertyName].unit, conversionContext),
                    source[propertyName].unit
                );
            } else {
                target[propertyName] = new UnitValue(
                    target[propertyName].toUnit(source[propertyName].unit),
                    source[propertyName].unit
                );
            }
        }
        // if value has changed (independent of unit, so 1 Pascal to 1000 milliPascal does not count as change, hence toBaseUnit)
        if (MeasurementUnitHelpers.hasValueChanged(source[propertyName], original[propertyName])) {
            // Just copy value and unit
            target[propertyName] = new UnitValue(source[propertyName].value, source[propertyName].unit);
        }
    }

    private copyPartialDischargeData(report: ReportableMeasurement<SorXMeasurement>): void {
        const source = this.measurement.data as SorXPDDData;
        const target = report.data as SorXPDDData;

        target.tagPDOperatingConditions = source.tagPDOperatingConditions;
    }
    //#endregion

    //#region Unit System and Currency Management
    private getSelectedUnitSystem(measurement: ReportableMeasurement<SorXMeasurement> | null): UnitSystem {
        if (this._selectedUnitSystem) return this._selectedUnitSystem;

        const unitSystem = MeasurementUnitHelpers.determineUnitSystem(measurement);

        // Also set the unit system so it corresponds with what we say it is
        if (measurement?.data && measurement.type === "SORXLD") {
            (measurement.data as SorXLDData).tagLeakUnitSystem = unitSystem;
        }
        this._selectedUnitSystem = unitSystem;

        return unitSystem;
    }

    private getSelectedCurrency(measurement: ReportableMeasurement<SorXMeasurement> | null): Unit {
        if (this._selectedCurrency) return this._selectedCurrency;

        const currency = MeasurementUnitHelpers.determineCurrency(measurement);
        this._selectedCurrency = currency;
        return currency;
    }
    //#endregion
}

// This needs to be on the bottom for it to work
export class EditMeasurementModalState {
    public onMeasurementsChanged?: () => void;
    public hasLeakRateConversionFeatureLicense?: boolean;
}
